// 계약 전 마진 시뮬레이터 — ops-britzmedi margin/Simulator.tsx 이식.
// (변경점: "계약 등록"이 blob 대신 onRegisterContract 콜백으로 위임 — Supabase
//  contracts 테이블로 승격(FR-7)은 FlowPage 가 담당. initialCountryId prop 추가.)
// 국가 탭별 기본조건 + 가격구간 레퍼런스 + 딜 패키지(가격안) 비교.
// 가격안 = MOQ 구간 계단 + 자유 비용 명목(고정/대당/매출%/총마진%)이 함께 움직이는 딜 조건 1벌.
import { useState } from "react";
import type { CountrySim, Offer, ProductState } from "@/lib/margin/model";
import {
  applicableTier,
  COST_BASES,
  effectiveTiers,
  krw,
  makeOffer,
  offerCalc,
  pct,
  tier,
  tierCalc,
  usd,
} from "@/lib/margin/model";
import { btn, neg, NumInput, TD, TH, TextInput } from "./ui";
import { OfferCompositionChart, PriceSweepChart } from "./MarginCharts";

/** BEST 배지 판정 기준 (사용자 선택, 브라우저에 기억). */
type BestCrit = "netKrw" | "netPerUnitKrw" | "marginPct";
const CRITS: { id: BestCrit; label: string }[] = [
  { id: "netKrw", label: "총 순이익" },
  { id: "netPerUnitKrw", label: "대당 순이익" },
  { id: "marginPct", label: "순이익률" },
];
const CRIT_LS = "marginDeskBestCrit";

export default function Simulator({
  product,
  onChange,
  onRegisterContract,
  initialCountryId,
}: {
  product: ProductState;
  onChange: (next: ProductState) => void;
  /** 가격안을 계약으로 승격 (FlowPage → contracts INSERT). 없으면 버튼 숨김. */
  onRegisterContract?: (c: CountrySim, o: Offer) => void;
  initialCountryId?: string;
}): JSX.Element {
  const [activeId, setActiveId] = useState(
    initialCountryId && product.countries.some((x) => x.id === initialCountryId)
      ? initialCountryId
      : (product.countries[0]?.id ?? ""),
  );
  const [crit, setCrit] = useState<BestCrit>(() => {
    const v = localStorage.getItem(CRIT_LS) as BestCrit | null;
    return v && CRITS.some((c) => c.id === v) ? v : "netKrw";
  });
  const pickCrit = (v: BestCrit) => {
    setCrit(v);
    localStorage.setItem(CRIT_LS, v);
  };

  const c = product.countries.find((x) => x.id === activeId) ?? product.countries[0];
  if (!c) return <p className="text-sm text-slate-500">국가가 없습니다. 국가를 추가하세요.</p>;

  const patch = (up: Partial<CountrySim>) =>
    onChange({
      ...product,
      countries: product.countries.map((x) => (x.id === c.id ? { ...x, ...up } : x)),
    });

  const addCountry = () => {
    const name = window.prompt("국가 이름 (예: 인도네시아)");
    if (!name) return;
    const id = (window.prompt("국가 코드 (예: ID)", name.slice(0, 2).toUpperCase()) || name).toUpperCase();
    if (product.countries.some((x) => x.id === id)) {
      window.alert("이미 있는 국가 코드입니다.");
      return;
    }
    const next: CountrySim = {
      ...c,
      id,
      name,
      tiers: c.tiers.map((t) => ({ ...t })),
      offers: c.offers.map((o) => ({ ...o, tiers: o.tiers.map((t) => ({ ...t })), costs: o.costs.map((x) => ({ ...x })) })),
    };
    onChange({ ...product, countries: [...product.countries, next] });
    setActiveId(id);
  };

  const removeCountry = () => {
    if (product.countries.length <= 1) return;
    if (!window.confirm(`'${c.name}' 탭과 그 기본조건·시뮬레이션을 삭제할까요? (계약·실적은 영향 없음)`)) return;
    const rest = product.countries.filter((x) => x.id !== c.id);
    onChange({ ...product, countries: rest });
    setActiveId(rest[0]!.id);
  };

  // ── 가격안 조작 헬퍼 ──
  const upOffer = (i: number, v: Partial<(typeof c.offers)[number]>) =>
    patch({ offers: c.offers.map((x, j) => (j === i ? { ...x, ...v } : x)) });

  const calcs = c.offers.map((o) => offerCalc(o, c));
  const vals = calcs.map((r) => r[crit]);
  const bestVal = Math.max(...vals.filter(Number.isFinite), -Infinity);
  const isBest = (i: number) => calcs.length > 1 && Number.isFinite(vals[i]!) && vals[i] === bestVal;
  const maxAbs = Math.max(...vals.filter(Number.isFinite).map(Math.abs), 1e-9);
  const critLabel = CRITS.find((k) => k.id === crit)!.label;
  const badge = (
    <span className="ml-1 rounded bg-emerald-600 px-1 py-0.5 align-middle text-[9px] font-semibold text-white">BEST</span>
  );
  const rowLabel =
    "border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 whitespace-nowrap";

  return (
    <div className="space-y-6">
      {/* 국가 탭 */}
      <div className="flex flex-wrap items-center gap-1">
        {product.countries.map((x) => (
          <button
            key={x.id}
            onClick={() => setActiveId(x.id)}
            className={
              "rounded-t border border-b-0 px-3 py-1.5 text-sm " +
              (x.id === c.id
                ? "border-slate-400 bg-white font-semibold text-slate-900"
                : "border-slate-200 bg-slate-100 text-slate-500 hover:text-slate-800")
            }
          >
            {x.name}
          </button>
        ))}
        <button onClick={addCountry} className={btn + " ml-2"}>
          + 국가 추가
        </button>
        <button onClick={removeCountry} className={btn + " text-red-500"}>
          현재 탭 삭제
        </button>
      </div>

      {/* 기본조건 */}
      <section>
        <h3 className="mb-2 text-sm font-bold text-slate-800">
          ■ {c.name} 기본 조건{" "}
          <span className="font-normal text-slate-400">(수정하면 자동 저장 — 이 탭의 기본조건이 됩니다)</span>
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <label className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="block text-xs font-semibold text-slate-700">제조원가 (KRW/대)</span>
            <NumInput value={c.costKrw} step={100_000} onChange={(v) => patch({ costKrw: v })} className="mt-1" />
          </label>
          <label className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="block text-xs font-semibold text-slate-700">환율 (USD→KRW)</span>
            <NumInput value={c.fx} step={10} onChange={(v) => patch({ fx: v })} className="mt-1" />
          </label>
          <label className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="block text-xs font-semibold text-slate-700">회사 플로어 (USD)</span>
            <NumInput value={c.floorCompany} step={500} onChange={(v) => patch({ floorCompany: v })} className="mt-1" />
            <span className="mt-1 block text-[11px] text-slate-400">정책 하한 · 인센티브 기준선</span>
          </label>
          <label className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="block text-xs font-semibold text-slate-700">참고 플로어 (USD)</span>
            <NumInput value={c.floorRef} step={500} onChange={(v) => patch({ floorRef: v })} className="mt-1" />
            <span className="mt-1 block text-[11px] text-slate-400">참고선 (수식 미사용)</span>
          </label>
          <label className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="block text-xs font-semibold text-slate-700">기본 수수료율 (%)</span>
            <NumInput
              value={Number.isFinite(c.rate) ? Math.round(c.rate * 1000) / 10 : NaN}
              step={0.5}
              onChange={(v) => patch({ rate: v / 100 })}
              className="mt-1"
            />
            <span className="mt-1 block text-[11px] text-slate-400">총 판매액 기준</span>
          </label>
        </div>
      </section>

      {/* 가격 구간 레퍼런스 (v1.1) */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">
            ■ 가격 구간별 대당 마진{" "}
            <span className="font-normal text-slate-400">
              (단가 레퍼런스 — 범위(부터~까지)를 채우면 아래 "기준안"에 자동 연동)
            </span>
          </h3>
          <button
            onClick={() =>
              patch({
                tiers: [...c.tiers, { label: `구간 ${c.tiers.length + 1}`, price: 0, fromQty: 1, toQty: null }],
              })
            }
            className={btn}
          >
            + 구간 추가
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {["구간", "부터", "까지", "판매가 USD", "판매가 KRW", "총마진 KRW", "총마진율", `기본수수료 ${pct(c.rate)}`, "인센티브 USD", "수수료 합계 USD", "수수료 KRW", "회사 순이익 KRW", ""].map(
                  (h, i) => (
                    <th key={i} className={TH}>
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {c.tiers.map((t, i) => {
                const r = tierCalc(t, c);
                return (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="border border-slate-200 px-1 py-1">
                      <TextInput
                        value={t.label}
                        onChange={(v) => patch({ tiers: c.tiers.map((x, j) => (j === i ? { ...x, label: v } : x)) })}
                        className="w-24 border-transparent bg-transparent hover:border-slate-300"
                      />
                    </td>
                    <td className="border border-slate-200 px-1 py-1">
                      <input
                        type="number"
                        placeholder="—"
                        value={t.fromQty ?? ""}
                        onChange={(e) =>
                          patch({
                            tiers: c.tiers.map((x, j) =>
                              j === i ? { ...x, fromQty: e.target.value === "" ? null : Number(e.target.value) } : x,
                            ),
                          })
                        }
                        className="w-14 rounded border border-slate-300 bg-white px-1 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
                      />
                    </td>
                    <td className="border border-slate-200 px-1 py-1">
                      <input
                        type="number"
                        placeholder="이상"
                        value={t.toQty ?? ""}
                        onChange={(e) =>
                          patch({
                            tiers: c.tiers.map((x, j) =>
                              j === i ? { ...x, toQty: e.target.value === "" ? null : Number(e.target.value) } : x,
                            ),
                          })
                        }
                        className="w-14 rounded border border-slate-300 bg-white px-1 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
                      />
                    </td>
                    <td className="border border-slate-200 px-1 py-1">
                      <NumInput
                        value={t.price}
                        step={500}
                        onChange={(v) => patch({ tiers: c.tiers.map((x, j) => (j === i ? { ...x, price: v } : x)) })}
                        className="w-24 border-amber-300 bg-amber-50"
                      />
                    </td>
                    <td className={TD}>{krw(r.priceKrw)}</td>
                    <td className={TD + neg(r.marginKrw)}>{krw(r.marginKrw)}</td>
                    <td className={TD}>{pct(r.marginPct)}</td>
                    <td className={TD}>{usd(r.base)}</td>
                    <td className={TD}>{usd(r.incentive)}</td>
                    <td className={TD + " font-semibold"}>{usd(r.commUsd)}</td>
                    <td className={TD}>{krw(r.commKrw)}</td>
                    <td className={TD + " font-bold" + neg(r.netKrw)}>{krw(r.netKrw)}</td>
                    <td className="border border-slate-200 px-1 text-center">
                      <button
                        onClick={() => patch({ tiers: c.tiers.filter((_, j) => j !== i) })}
                        className="text-xs text-slate-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── 딜 패키지(가격안) 비교 ─────────────────────────────────────────── */}
      <section>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-bold text-slate-800">
            ■ 딜 조건(가격안) 비교 — {c.name}{" "}
            <span className="font-normal text-slate-400">(안 = MOQ 구간 계단 + 비용 명목이 함께 움직이는 패키지)</span>
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">BEST 기준</span>
            <div className="flex overflow-hidden rounded border border-slate-300">
              {CRITS.map((k) => (
                <button
                  key={k.id}
                  onClick={() => pickCrit(k.id)}
                  className={
                    "px-2 py-1 text-xs " +
                    (crit === k.id ? "bg-slate-800 font-semibold text-white" : "bg-white text-slate-600 hover:bg-slate-100")
                  }
                >
                  {k.label}
                </button>
              ))}
            </div>
            <button
              onClick={() =>
                patch({
                  offers: [
                    ...c.offers,
                    makeOffer(`${String.fromCharCode(65 + c.offers.filter((o) => !o.linked).length)}안`),
                  ],
                })
              }
              className={btn}
            >
              + 비교안 추가
            </button>
          </div>
        </div>

        {/* 안별 편집 카드 (가로 나열) */}
        <div className="flex gap-4 overflow-x-auto pb-2">
          {c.offers.map((o, i) => {
            const r = calcs[i]!;
            return (
              <div
                key={i}
                className={
                  "w-[340px] shrink-0 rounded-lg border bg-white p-3 " +
                  (isBest(i) ? "border-emerald-400 ring-1 ring-emerald-200" : "border-slate-200")
                }
              >
                <div className="mb-2 flex items-center gap-1">
                  <TextInput
                    value={o.label}
                    onChange={(v) => upOffer(i, { label: v })}
                    className="border-transparent bg-transparent text-sm font-bold hover:border-slate-300"
                  />
                  {o.linked && (
                    <span className="shrink-0 rounded bg-blue-600 px-1 py-0.5 text-[9px] font-semibold text-white">연동</span>
                  )}
                  {isBest(i) && badge}
                  {!o.linked && (
                    <button
                      onClick={() => c.offers.length > 1 && patch({ offers: c.offers.filter((_, j) => j !== i) })}
                      className="ml-auto text-xs text-slate-400 hover:text-red-600"
                      title="안 삭제"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* 예상 주문 수량 */}
                <div className="mb-2 flex items-center gap-2 rounded bg-amber-50 px-2 py-1.5">
                  <span className="text-[11px] font-semibold text-slate-600">예상 주문 수량</span>
                  <NumInput
                    value={o.orderQty}
                    onChange={(v) => upOffer(i, { orderQty: v })}
                    className="w-20 border-amber-300"
                  />
                  <span className="text-[11px] text-slate-500">대 → 걸리는 구간 단가 전량 적용</span>
                </div>

                {/* MOQ 구간표 — 기준안은 레퍼런스 연동(읽기 전용), 비교안은 자체 편집 */}
                {o.linked ? (
                  <>
                    <p className="mb-1 text-[11px] font-semibold text-blue-600">
                      구간표 = 위 "가격 구간별 대당 마진" 표와 실시간 연동 (범위·단가 수정은 위 표에서)
                    </p>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["범위", "단가 USD", ""].map((h, hi) => (
                            <th key={hi} className="border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {effectiveTiers(o, c).map((t, ti) => {
                          const active = applicableTier(effectiveTiers(o, c), o.orderQty).index === ti;
                          return (
                            <tr key={ti} className={active ? "bg-emerald-50" : ""}>
                              <td className="border border-slate-200 px-2 py-1 text-sm tabular-nums">
                                {t.fromQty}
                                {t.toQty != null ? `–${t.toQty}` : "+"}대
                              </td>
                              <td className="border border-slate-200 px-2 py-1 text-right text-sm tabular-nums">
                                ${usd(t.price)}
                              </td>
                              <td className="w-6 border border-slate-200 text-center">{active && "◀"}</td>
                            </tr>
                          );
                        })}
                        {effectiveTiers(o, c).length === 0 && (
                          <tr>
                            <td colSpan={3} className="border border-slate-200 px-2 py-2 text-xs text-slate-400">
                              위 표에서 구간의 "부터/까지"를 채우면 여기에 연동됩니다.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <>
                    <p className="mb-1 text-[11px] font-semibold text-slate-500">
                      MOQ 구간표 (시작~끝 수량 · 단가 USD) — 끝 비우면 "이상"
                    </p>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          {["부터", "까지", "단가", ""].map((h, hi) => (
                            <th key={hi} className="border border-slate-200 bg-slate-50 px-1 py-0.5 text-[10px] font-medium text-slate-500">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {o.tiers.map((t, ti) => {
                          const active = applicableTier(o.tiers, o.orderQty).index === ti;
                          return (
                            <tr key={ti} className={active ? "bg-emerald-50" : ""}>
                              <td className="border border-slate-200 p-0.5">
                                <NumInput
                                  value={t.fromQty}
                                  onChange={(v) =>
                                    upOffer(i, { tiers: o.tiers.map((x, j) => (j === ti ? { ...x, fromQty: v } : x)) })
                                  }
                                  className="border-amber-300 bg-amber-50"
                                />
                              </td>
                              <td className="border border-slate-200 p-0.5">
                                <input
                                  type="number"
                                  placeholder="이상"
                                  value={t.toQty ?? ""}
                                  onChange={(e) =>
                                    upOffer(i, {
                                      tiers: o.tiers.map((x, j) =>
                                        j === ti ? { ...x, toQty: e.target.value === "" ? null : Number(e.target.value) } : x,
                                      ),
                                    })
                                  }
                                  className="w-full rounded border border-amber-300 bg-amber-50 px-2 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
                                />
                              </td>
                              <td className="border border-slate-200 p-0.5">
                                <NumInput
                                  value={t.price}
                                  step={500}
                                  onChange={(v) =>
                                    upOffer(i, { tiers: o.tiers.map((x, j) => (j === ti ? { ...x, price: v } : x)) })
                                  }
                                  className="border-amber-300 bg-amber-50"
                                />
                              </td>
                              <td className="w-6 border border-slate-200 text-center">
                                {active && <span title="주문 수량이 걸린 구간">◀</span>}
                                <button
                                  onClick={() => o.tiers.length > 1 && upOffer(i, { tiers: o.tiers.filter((_, j) => j !== ti) })}
                                  className="text-xs text-slate-400 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <button
                      onClick={() => {
                        const last = o.tiers[o.tiers.length - 1];
                        const from = last?.toQty != null ? last.toQty + 1 : (last?.fromQty ?? 0) + 10;
                        upOffer(i, { tiers: [...o.tiers, tier(from, null, 0)] });
                      }}
                      className={btn + " mt-1 w-full"}
                    >
                      + 구간 추가
                    </button>
                  </>
                )}

                {/* 무상 제공 조건 — 구간과 독립 (예: 누적 30대당 1대) */}
                <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-2 py-1.5">
                  <p className="mb-1 text-[11px] font-semibold text-slate-500">무상 제공 조건 (구간과 무관, 딜 전체 기준)</p>
                  <div className="flex flex-wrap items-center gap-1 text-xs text-slate-600">
                    <span>고정</span>
                    <NumInput
                      value={o.free.freeFixed}
                      onChange={(v) => upOffer(i, { free: { ...o.free, freeFixed: Number.isFinite(v) ? v : 0 } })}
                      className="w-14"
                    />
                    <span>대 + 누적</span>
                    <input
                      type="number"
                      placeholder="없음"
                      value={o.free.freePerN ?? ""}
                      onChange={(e) =>
                        upOffer(i, { free: { ...o.free, freePerN: e.target.value === "" ? null : Number(e.target.value) } })
                      }
                      className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
                    />
                    <span>대당</span>
                    <NumInput
                      value={o.free.freePerUnits}
                      onChange={(v) => upOffer(i, { free: { ...o.free, freePerUnits: Number.isFinite(v) ? v : 0 } })}
                      className="w-14"
                    />
                    <span>대 무상</span>
                    {r.freeUnits > 0 && (
                      <span className="ml-auto rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                        이 주문에서 무상 {r.freeUnits}대 발생
                      </span>
                    )}
                  </div>
                </div>

                {/* 비용 명목 (자유 추가) */}
                <p className="mb-1 mt-3 text-[11px] font-semibold text-slate-500">비용 명목 (명목 · 기준 · 값)</p>
                {o.costs.map((ci, cidx) => (
                  <div key={cidx} className="mb-1 flex items-center gap-1">
                    <TextInput
                      value={ci.name}
                      placeholder="명목 (운임, 설치…)"
                      onChange={(v) => upOffer(i, { costs: o.costs.map((x, j) => (j === cidx ? { ...x, name: v } : x)) })}
                      className="w-28"
                    />
                    <select
                      value={ci.base}
                      onChange={(e) =>
                        upOffer(i, {
                          costs: o.costs.map((x, j) => (j === cidx ? { ...x, base: e.target.value as typeof ci.base } : x)),
                        })
                      }
                      className="rounded border border-slate-300 bg-white px-1 py-1 text-xs"
                    >
                      {COST_BASES.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                    <NumInput
                      value={ci.value}
                      step={ci.base.startsWith("pct") ? 0.5 : 100_000}
                      onChange={(v) => upOffer(i, { costs: o.costs.map((x, j) => (j === cidx ? { ...x, value: v } : x)) })}
                      className="w-24"
                    />
                    <button
                      onClick={() => upOffer(i, { costs: o.costs.filter((_, j) => j !== cidx) })}
                      className="text-xs text-slate-400 hover:text-red-600"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => upOffer(i, { costs: [...o.costs, { name: "", base: "fixed_krw", value: 0 }] })}
                  className={btn + " w-full"}
                >
                  + 비용 명목 추가
                </button>

                {/* 계약 등록 — 이 안의 구간표·무상조건을 정식 계약으로 승격 (FR-7) */}
                {onRegisterContract && (
                  <button
                    onClick={() => {
                      const ts = effectiveTiers(o, c);
                      if (ts.length === 0) {
                        window.alert("구간표가 비어 있어 계약으로 등록할 수 없습니다.");
                        return;
                      }
                      onRegisterContract(c, o);
                    }}
                    className={btn + " mt-2 w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"}
                  >
                    ✓ 이 안으로 계약 등록 (계약 → 선적 딜 → 실적으로 연결)
                  </button>
                )}

                {/* 수수료 특례 + 메모 */}
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[11px] text-slate-500">수수료율 특례(%)</span>
                  <input
                    type="number"
                    step={0.5}
                    placeholder={`기본 ${(c.rate * 100).toFixed(1)}`}
                    value={o.rateOverride == null ? "" : Math.round(o.rateOverride * 1000) / 10}
                    onChange={(e) => upOffer(i, { rateOverride: e.target.value === "" ? null : Number(e.target.value) / 100 })}
                    className="w-20 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-slate-500 focus:outline-none"
                  />
                  <TextInput value={o.note} onChange={(v) => upOffer(i, { note: v })} placeholder="조건 메모" className="flex-1" />
                </div>

                {/* 카드 내 산출 요약 — 지출을 명목별로 분해 표시 */}
                <div className="mt-3 rounded bg-slate-50 p-2 text-xs leading-relaxed">
                  <div className="flex justify-between">
                    <span className="text-slate-500">주문 {usd(r.units)}대{r.freeUnits > 0 ? ` (+무상 ${r.freeUnits})` : ""}</span>
                    <span className="tabular-nums">적용 단가 ${usd(r.avgPriceUsd)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">총 매출</span>
                    <span className="tabular-nums">
                      ${usd(r.revenueUsd)} = {krw(r.revenueKrw)}원
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">− 제조원가{r.freeUnits > 0 ? ` (무상 ${r.freeUnits}대 포함)` : ""}</span>
                    <span className="tabular-nums text-slate-700">{krw(r.costMfg)}원</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">− 판매수수료 ({pct(r.rate)}, 총판매액 기준)</span>
                    <span className="tabular-nums text-slate-700">{krw(r.costCommBase)}원</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">− 차액 인센티브 (플로어 ${usd(c.floorCompany)} 초과분)</span>
                    <span className="tabular-nums text-slate-700">{krw(r.costCommIncentive)}원</span>
                  </div>
                  {r.customCosts.map((cc, k) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-slate-500">
                        − {cc.name || "기타 명목"}{" "}
                        <span className="text-slate-400">
                          ({COST_BASES.find((b) => b.id === cc.base)?.label}
                          {cc.base.startsWith("pct") ? ` ${cc.value}%` : ""})
                        </span>
                      </span>
                      <span className="tabular-nums text-slate-700">{krw(cc.amountKrw)}원</span>
                    </div>
                  ))}
                  <div className="flex justify-between border-t border-slate-200 pt-0.5 font-semibold">
                    <span className="text-slate-600">지출 합계</span>
                    <span className="tabular-nums">{krw(r.costTotal)}원</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-1 font-bold">
                    <span>회사 순이익</span>
                    <span className={"tabular-nums" + neg(r.netKrw)}>{krw(r.netKrw)}원</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>대당 {krw(r.netPerUnitKrw)}원</span>
                    <span>이익률 {pct(r.marginPct)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 요약 비교 테이블 (지표 × 안) */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={TH}>지표 \ 안</th>
                {c.offers.map((o, i) => (
                  <th key={i} className={TH + " min-w-36"}>
                    {o.label}
                    {isBest(i) && badge}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(
                [
                  [
                    "구간표 · 주문수량",
                    (i: number) =>
                      effectiveTiers(c.offers[i]!, c)
                        .map((t) => `${t.fromQty}${t.toQty != null ? `–${t.toQty}` : "+"}@${usd(t.price)}`)
                        .join(" · ") +
                      (c.offers[i]!.free.freePerN ? ` · 무상 ${c.offers[i]!.free.freePerN}대당${c.offers[i]!.free.freePerUnits}` : "") +
                      (c.offers[i]!.free.freeFixed ? ` · 고정무상${c.offers[i]!.free.freeFixed}` : "") +
                      ` ▶ ${usd(calcs[i]!.units)}대 주문`,
                  ],
                  ["적용 단가 USD", (i: number) => "$" + usd(calcs[i]!.avgPriceUsd)],
                  ["총 매출 KRW", (i: number) => krw(calcs[i]!.revenueKrw)],
                  ["지출 · 제조원가", (i: number) => krw(calcs[i]!.costMfg)],
                  ["지출 · 판매수수료", (i: number) => `${krw(calcs[i]!.costCommBase)} (${pct(calcs[i]!.rate)})`],
                  ["지출 · 차액 인센티브", (i: number) => (calcs[i]!.costCommIncentive > 0 ? krw(calcs[i]!.costCommIncentive) : "—")],
                  // 비용 명목은 명목 이름별로 개별 행 (전 안에 걸친 합집합)
                  ...[...new Set(calcs.flatMap((r2) => r2.customCosts.map((cc) => cc.name || "기타 명목")))].map(
                    (name): [string, (i: number) => string] => [
                      `지출 · ${name}`,
                      (i: number) => {
                        const items = calcs[i]!.customCosts.filter((cc) => (cc.name || "기타 명목") === name);
                        if (items.length === 0) return "—";
                        const amt = items.reduce((s, cc) => s + cc.amountKrw, 0);
                        const pctItem = items.find((cc) => cc.base.startsWith("pct"));
                        return krw(amt) + (pctItem ? ` (${pctItem.value}%)` : "");
                      },
                    ],
                  ),
                  ["지출 합계", (i: number) => krw(calcs[i]!.costTotal)],
                ] as [string, (i: number) => string][]
              ).map(([lab, fn]) => (
                <tr key={lab}>
                  <td className={rowLabel}>{lab}</td>
                  {c.offers.map((_, i) => (
                    <td key={i} className={TD + (isBest(i) ? " bg-emerald-50" : "")}>
                      {fn(i)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300">
                <td className={rowLabel + " font-bold"}>회사 순이익 KRW</td>
                {calcs.map((r, i) => (
                  <td key={i} className={TD + (isBest(i) ? " bg-emerald-50" : "") + " text-base font-bold" + neg(r.netKrw)}>
                    {krw(r.netKrw)}
                    {crit === "netKrw" && isBest(i) && badge}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={rowLabel}>대당 순이익 KRW</td>
                {calcs.map((r, i) => (
                  <td key={i} className={TD + (isBest(i) ? " bg-emerald-50" : "") + neg(r.netPerUnitKrw)}>
                    {krw(r.netPerUnitKrw)}
                    {crit === "netPerUnitKrw" && isBest(i) && badge}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={rowLabel}>순이익률</td>
                {calcs.map((r, i) => (
                  <td key={i} className={TD + (isBest(i) ? " bg-emerald-50" : "")}>
                    {pct(r.marginPct)}
                    {crit === "marginPct" && isBest(i) && badge}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={rowLabel}>{critLabel} 비교</td>
                {calcs.map((_, i) => {
                  const v = vals[i]!;
                  return (
                    <td key={i} className="border border-slate-200 px-2 py-2 align-middle">
                      <div className="h-3 w-full rounded bg-slate-100">
                        <div
                          className={"h-3 rounded " + (v < 0 ? "bg-red-400" : isBest(i) ? "bg-emerald-500" : "bg-slate-400")}
                          style={{ width: `${Math.min(100, Math.round((Math.abs(v) / maxAbs) * 100))}%` }}
                        />
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          ※ 수수료 = 구간 단가별 [단가×적용율(특례 없으면 기본 {pct(c.rate)}) + MAX(0, 단가−플로어 ${usd(c.floorCompany)})]
          합산 · 무상분은 매출 없이 제조원가만 발생 · 비용 명목: 고정/대당은 KRW, %는 매출 또는 총마진(매출−제조원가) 기준 ·
          BEST/초록 = 선택 기준 최대 안 (총 순이익 기준은 물량 큰 안에 유리 — 대당·이익률과 교차 확인) · 관세·현지 세금 미포함
        </p>

        {/* 시각 비교 — 구성 스택 + 가격 스윕 */}
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h4 className="text-xs font-bold text-slate-800">안별 매출 구성 — 지출과 순이익이 매출에서 차지하는 몫</h4>
            <p className="mb-2 text-[11px] text-slate-400">막대 전체 = 총매출(KRW). 위 초록 = 회사 순이익.</p>
            <div className="h-72">
              <OfferCompositionChart c={c} />
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h4 className="text-xs font-bold text-slate-800">판매가에 따른 대당 손익 곡선 — 구간 단가들의 위치</h4>
            <p className="mb-2 text-[11px] text-slate-400">
              플로어 ${usd(c.floorCompany)} 초과분은 인센티브로 빠져나가 곡선이 꺾입니다. 파란 점 = 각 안의 구간 단가.
            </p>
            <div className="h-72">
              <PriceSweepChart c={c} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
